// reactiveLocalStore — a tiny reactive, localStorage-backed store factory shared across features.
//
// A module-level store per key + `useSyncExternalStore`: a write in one component re-renders every
// component that SUBSCRIBES (reads the reactive field the consuming hook exposes). Extracted from the
// wells spine store (features/wells/store/spineStore.ts) once a SECOND feature (completions) needed it,
// so neither feature owns the other's infra (kills the wells↔completions backward coupling — P3a
// adversarial review, Sev-1). Feature stores build on this; they own their KEYS and value types.
//
// Charter Abs-2: pure state container — NO engineering/economic/statistical math.

type Listener = () => void;

export interface ReactiveLocalStore<T> {
  /** The localStorage key this store persists to. */
  readonly key: string;
  /** Current value. Returns a STABLE reference while the underlying localStorage string is unchanged
   *  (required by useSyncExternalStore — a fresh array each call would loop). Re-reads localStorage on
   *  every call and reconciles the cache, so a direct `localStorage.setItem` (e.g. a test seed) or a
   *  cross-tab write is picked up. */
  read: () => T[];
  /** Replace the whole array: persist to localStorage, refresh the cache (stable ref), notify. */
  setAll: (next: T[]) => void;
  /** Subscribe to changes (same-tab writes + cross-tab `storage` events). Returns an unsubscribe fn. */
  subscribe: (listener: Listener) => () => void;
}

/** A sentinel distinct from any possible localStorage value (string | null), so the first read always
 *  misses the cache and reconciles. */
const UNREAD = Symbol("unread");

export function createReactiveLocalStore<T>(
  key: string,
): ReactiveLocalStore<T> {
  let cachedRaw: string | null | typeof UNREAD = UNREAD;
  let cachedValue: T[] = [];
  const listeners = new Set<Listener>();

  // When the last write couldn't persist (quota exceeded / private mode), the in-memory cache is
  // AUTHORITATIVE for the session: localStorage still holds the stale prior value, so reconciling
  // read() against it would silently drop the just-written data. Large demo datasets legitimately
  // exceed the ~5MB localStorage quota (e.g. the 350-well OpenWells import ≈ 30MB of wells).
  let unpersisted = false;
  const read = (): T[] => {
    if (unpersisted) return cachedValue;
    let raw: string | null;
    try {
      raw = localStorage.getItem(key);
    } catch {
      raw = null; // storage blocked → behave as empty (demo fallback, never throws)
    }
    if (raw === cachedRaw) return cachedValue; // unchanged → stable reference
    cachedRaw = raw;
    if (!raw) {
      cachedValue = [];
      return cachedValue;
    }
    try {
      const parsed: unknown = JSON.parse(raw);
      cachedValue = Array.isArray(parsed) ? (parsed as T[]) : [];
    } catch {
      cachedValue = []; // corrupt → empty
    }
    return cachedValue;
  };

  const setAll = (next: T[]): void => {
    let serialized: string | null = null;
    try {
      serialized = JSON.stringify(next);
      localStorage.setItem(key, serialized);
      unpersisted = false;
    } catch {
      // Storage unavailable (private mode / quota) — keep the in-memory value AUTHORITATIVE for this
      // session (read() returns the cache without reconciling against stale storage) so the UI still
      // updates; persistence is a demo-path best-effort. Large demo datasets exceed the ~5MB quota.
      unpersisted = true;
    }
    // Adopt `next` as the cache with a stable reference so subscribers reading via read() get exactly
    // this array (and read() short-circuits until localStorage changes again).
    cachedRaw = serialized;
    cachedValue = next;
    listeners.forEach((l) => l());
  };

  const subscribe = (listener: Listener): (() => void) => {
    listeners.add(listener);
    const onStorage = (e: StorageEvent) => {
      if (e.key === key || e.key === null) listener(); // null key = storage.clear()
    };
    window.addEventListener("storage", onStorage);
    return () => {
      listeners.delete(listener);
      window.removeEventListener("storage", onStorage);
    };
  };

  return { key, read, setAll, subscribe };
}
